import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
  Slider
} from '@mui/material';
import {
  Add,
  Delete,
  Save,
  Cancel,
  Code,
  Description,
  Quiz,
  Upload,
  Link as LinkIcon,
  Work
} from '@mui/icons-material';
import { InputAdornment } from '@mui/material';
import axios from 'axios';

const EditTask = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [jobs, setJobs] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    difficulty: 'medium',
    taskType: 'essay',
    instructions: '',
    duration: {
      value: 168, // Default 168 hours (7 days)
      unit: 'hours'
    },
    deadline: '',
    maxScore: 100,
    passingScore: 60,
    autoEvaluate: false,
    pipelineStage: 'Assessment',
    isActive: true
  });

  const [questions, setQuestions] = useState([]);
  const [codeTemplate, setCodeTemplate] = useState({
    language: 'javascript',
    starterCode: '',
    testCases: []
  });
  const [evaluationCriteria, setEvaluationCriteria] = useState([]);
  const [resources, setResources] = useState([]);

  useEffect(() => {
    fetchTask();
    fetchJobs();
  }, [id]);

  const fetchTask = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `http://localhost:5000/api/tasks/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const task = response.data.data;
      
      // Format deadline for datetime-local input
      const deadline = task.deadline ? new Date(task.deadline).toISOString().slice(0, 16) : '';
      
      // Handle duration - check for new format first, then fall back to old timeLimit
      let durationValue = 168; // Default 7 days in hours
      let durationUnit = 'hours';
      
      if (task.duration && task.duration.value && task.duration.unit) {
        // New format exists
        if (task.duration.unit === 'hours') {
          durationValue = task.duration.value;
        } else if (task.duration.unit === 'days') {
          durationValue = task.duration.value * 24;
        } else if (task.duration.unit === 'weeks') {
          durationValue = task.duration.value * 24 * 7;
        }
      } else if (task.timeLimit) {
        // Old format - convert days to hours
        durationValue = task.timeLimit * 24;
      }
      
      setFormData({
        title: task.title || '',
        description: task.description || '',
        category: task.category || 'other',
        difficulty: task.difficulty || 'medium',
        taskType: task.taskType || 'essay',
        instructions: task.instructions || '',
        duration: {
          value: durationValue,
          unit: 'hours'
        },
        deadline: deadline,
        maxScore: task.maxScore || 100,
        passingScore: task.passingScore || 60,
        autoEvaluate: task.autoEvaluate || false,
        pipelineStage: task.pipelineStage || 'Assessment',
        isActive: task.isActive !== undefined ? task.isActive : true
      });
      
      setQuestions(task.questions || []);
      setCodeTemplate(task.codeTemplate || {
        language: 'javascript',
        starterCode: '',
        testCases: []
      });
      setEvaluationCriteria(task.evaluationCriteria || []);
      setResources(task.resources || []);
      setSelectedJobs(task.mappedJobs || []);
      
    } catch (err) {
      console.error('Error fetching task:', err);
      setError('Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('http://localhost:5000/api/jobs', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.data || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData({
      ...formData,
      [name]: e.target.type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare task data
      let taskData = {
        ...formData,
        mappedJobs: selectedJobs.map(job => job._id || job),
        evaluationCriteria,
        resources
      };

      // Add type-specific data
      if (formData.taskType === 'multiple-choice') {
        taskData.questions = questions;
      } else if (formData.taskType === 'code') {
        taskData.codeTemplate = codeTemplate;
      }

      await axios.put(
        `http://localhost:5000/api/tasks/${id}`,
        taskData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccess('Task updated successfully!');
      setTimeout(() => navigate('/tasks'), 2000);
    } catch (err) {
      console.error('Error updating task:', err);
      setError(err.response?.data?.message || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  const getTaskTypeIcon = (type) => {
    const icons = {
      'multiple-choice': <Quiz />,
      'essay': <Description />,
      'code': <Code />,
      'file-upload': <Upload />,
      'url-submission': <LinkIcon />,
      'project': <Work />
    };
    return icons[type] || <Description />;
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Edit Task
      </Typography>
      
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Task Title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  label="Category"
                >
                  <MenuItem value="coding">Coding</MenuItem>
                  <MenuItem value="design">Design</MenuItem>
                  <MenuItem value="writing">Writing</MenuItem>
                  <MenuItem value="analytical">Analytical</MenuItem>
                  <MenuItem value="behavioral">Behavioral</MenuItem>
                  <MenuItem value="case-study">Case Study</MenuItem>
                  <MenuItem value="presentation">Presentation</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleChange}
                  label="Difficulty"
                >
                  <MenuItem value="easy">Easy</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="hard">Hard</MenuItem>
                  <MenuItem value="expert">Expert</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Task Type</InputLabel>
                <Select
                  name="taskType"
                  value={formData.taskType}
                  onChange={handleChange}
                  label="Task Type"
                  startAdornment={getTaskTypeIcon(formData.taskType)}
                  disabled // Can't change task type after creation
                >
                  <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                  <MenuItem value="essay">Essay/Writing</MenuItem>
                  <MenuItem value="code">Coding</MenuItem>
                  <MenuItem value="file-upload">File Upload</MenuItem>
                  <MenuItem value="url-submission">URL Submission</MenuItem>
                  <MenuItem value="project">Project</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Instructions"
                name="instructions"
                value={formData.instructions}
                onChange={handleChange}
                placeholder="Provide clear instructions for candidates"
              />
            </Grid>
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Time and Scoring */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Time & Scoring
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (in hours)"
                type="number"
                value={formData.duration.value}
                onChange={(e) => setFormData({
                  ...formData,
                  duration: {
                    value: parseInt(e.target.value) || 1,
                    unit: 'hours'
                  }
                })}
                InputProps={{
                  inputProps: { min: 1, max: 720 },
                  endAdornment: <InputAdornment position="end">hours</InputAdornment>
                }}
                helperText="Enter duration in hours (e.g., 24 for 1 day, 168 for 1 week)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deadline"
                name="deadline"
                type="datetime-local"
                value={formData.deadline}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Maximum Score"
                name="maxScore"
                type="number"
                value={formData.maxScore}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 1, max: 1000 } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Passing Score"
                name="passingScore"
                type="number"
                value={formData.passingScore}
                onChange={handleChange}
                InputProps={{ inputProps: { min: 0, max: formData.maxScore } }}
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    name="autoEvaluate"
                    checked={formData.autoEvaluate}
                    onChange={handleChange}
                  />
                }
                label="Auto-Evaluate"
              />
            </Grid>
            
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>
            
            <Divider sx={{ width: '100%', my: 2 }} />
            
            {/* Job Mapping */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Map to Jobs
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={jobs}
                getOptionLabel={(option) => `${option.title} - ${option.department}`}
                value={selectedJobs}
                onChange={(e, value) => setSelectedJobs(value)}
                isOptionEqualToValue={(option, value) => 
                  (option._id || option) === (value._id || value)
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.title || option}
                        {...tagProps}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Jobs"
                    placeholder="Choose jobs to map this task to"
                  />
                )}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Pipeline Stage</InputLabel>
                <Select
                  name="pipelineStage"
                  value={formData.pipelineStage}
                  onChange={handleChange}
                  label="Pipeline Stage"
                >
                  <MenuItem value="Screening">Screening</MenuItem>
                  <MenuItem value="Assessment">Assessment</MenuItem>
                  <MenuItem value="Interview">Interview</MenuItem>
                  <MenuItem value="Final Review">Final Review</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error">{error}</Alert>
              </Grid>
            )}
            
            {success && (
              <Grid item xs={12}>
                <Alert severity="success">{success}</Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => navigate('/tasks')}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  disabled={saving}
                >
                  {saving ? <CircularProgress size={24} /> : 'Update Task'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
};

export default EditTask;
